import { createFileRoute } from '@tanstack/react-router'
import {
  Landmark,
  CreditCard,
  HeartHandshake,
  LineChart,
  ArrowRight,
  Boxes,
  RefreshCw,
  ShieldCheck,
} from 'lucide-react'
import { motion } from 'framer-motion'
import {
  Container,
  PageHero,
  SectionHeading,
  FeatureCard,
  CtaBand,
  fadeUp,
} from '@/components/umbrella/ui'

export const Route = createFileRoute('/solutions/')({
  component: SolutionsPage,
})

const AUDIENCES = [
  {
    Icon: Landmark,
    iconBg: 'bg-blue-600',
    title: 'State Agencies',
    body: 'State-specific scam education, awareness campaigns, and reporting guidance tailored to your residents and routed to your own hotlines.',
    href: '/solutions/state-agencies',
  },
  {
    Icon: CreditCard,
    iconBg: 'bg-slate-900',
    title: 'Banks & Credit Unions',
    body: 'Education that helps account holders pause before money moves, delivered by the institution they already trust. Includes a tailored program for defense credit unions.',
    href: '/solutions/financial-institutions',
  },
  {
    Icon: LineChart,
    iconBg: 'bg-emerald-600',
    title: 'Wealth Advisors & RIAs',
    body: 'Client and family scam-defense education under your practice’s brand. Protects clients between meetings and builds real relationships with the next generation.',
    href: '/solutions/wealth-advisors',
  },
  {
    Icon: HeartHandshake,
    iconBg: 'bg-rose-500',
    title: 'Community Partners',
    body: 'Ready-to-share education, workshops, and printable materials for libraries, nonprofits, senior centers, employers, and faith communities.',
    href: '/solutions/community-partners',
  },
]

const SHARED = [
  {
    Icon: Boxes,
    title: 'One proven platform',
    body: 'Every deployment runs on the same education, tools, and scam library, refined across audiences instead of built from scratch for each one.',
  },
  {
    Icon: RefreshCw,
    title: 'Always current',
    body: 'Content is continuously reviewed and updated as scam tactics change, so your resource never goes stale.',
  },
  {
    Icon: ShieldCheck,
    title: 'Private and accessible',
    body: 'No account required, no personal data sold, and built to work for every level of tech confidence.',
  },
]

function SolutionsPage() {
  return (
    <div className="bg-white">
      <PageHero
        label="Solutions"
        title="One platform, tailored for"
        highlight="different missions."
        blurb="ScamDefense is a single, proven scam-prevention platform that we theme, configure, and deploy for the audience you serve. Same trusted education underneath, shaped to fit your community and your brand."
        primary={{ label: 'Talk to our team', href: '#contact' }}
      />

      <Container className="py-20">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {AUDIENCES.map((a, i) => (
            <motion.a
              key={a.title}
              href={a.href}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: i * 0.08 }}
              className="group flex flex-col rounded-2xl border border-slate-200/80 bg-white p-7 transition hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md"
            >
              <span className={`flex h-12 w-12 items-center justify-center rounded-full ${a.iconBg} text-white`}>
                <a.Icon className="h-6 w-6" />
              </span>
              <h3 className="mt-5 text-lg font-semibold text-slate-900">{a.title}</h3>
              <p className="mt-3 flex-1 text-[15px] leading-relaxed text-slate-600">{a.body}</p>
              <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600">
                Explore
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </span>
            </motion.a>
          ))}
        </div>
      </Container>

      <section className="bg-slate-50 py-20">
        <Container>
          <SectionHeading
            label="Why one platform"
            title="Build once, serve everyone"
            blurb="Every deployment runs on the same foundation, so you get a tailored experience without the cost or risk of building from scratch."
          />
          <div className="mt-12 grid gap-5 md:grid-cols-3">
            {SHARED.map(s => (
              <FeatureCard key={s.title} {...s} />
            ))}
          </div>
        </Container>
      </section>

      <CtaBand />
    </div>
  )
}
